import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Tabs from '@cloudscape-design/components/tabs';
import { useAnalyticsConsentUi } from '@/context/AnalyticsConsentUiContext';
import { formatVersionDescription } from '@utils/version';
import { getGaMeasurementId, sendGtagPageView } from '@utils/analytics';
import {
  TAB_ID_TO_PAGE_TITLE,
  pathForTabId,
  tabIdFromPathname,
  type ToolTabId,
} from '@utils/toolRoutes';
import LandingPage from '@pages/LandingPage';
import InvisibleCharactersTool from '@pages/InvisibleCharactersTool';
import InvisibleCharacterLegendTool from '@pages/InvisibleCharacterLegendTool';
import SafeLinksTool from '@pages/SafeLinksTool';
import MailHeaderTool from '@pages/MailHeaderTool';
import UrlSafetyTool from '@pages/UrlSafetyTool';
import MailEmlAnalyzerTool from '@pages/MailEmlAnalyzerTool';

const APP_TITLE_SUFFIX = 'Unicode Security Analyzer';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openCookieSettings } = useAnalyticsConsentUi();
  const gaMeasurementId = getGaMeasurementId();
  const prevTrackedPathRef = useRef<string | null>(null);

  const resolvedTabId: ToolTabId = tabIdFromPathname(location.pathname) ?? 'home';

  useEffect(() => {
    if (tabIdFromPathname(location.pathname) === undefined && location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const pageTitle = TAB_ID_TO_PAGE_TITLE[resolvedTabId];
    document.title = `${pageTitle} · ${APP_TITLE_SUFFIX}`;
  }, [resolvedTabId]);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    const pageTitle = `${TAB_ID_TO_PAGE_TITLE[resolvedTabId]} · ${APP_TITLE_SUFFIX}`;
    const prev = prevTrackedPathRef.current;
    prevTrackedPathRef.current = path;
    if (prev === null || prev === path) {
      return;
    }
    sendGtagPageView(path, pageTitle);
  }, [location.pathname, location.search, resolvedTabId]);

  const handleToolSelection = (toolId: string) => {
    navigate(pathForTabId(toolId));
  };

  return (
    <ContentLayout
      disableOverlap
      header={
        <div className="portfolio-wizard-header-slot-wrap">
          <Header
            variant="h1"
            description={
              <>
                {formatVersionDescription()}
                {' • '}
                <Link href="https://www.svenesis.org/contact/contact_en.html" external>
                  Contact & Legal Notice
                </Link>
                {' • '}
                <Link href="https://www.svenesis.org/privacy/privacy_en.html" external>
                  Data Privacy
                </Link>
                {gaMeasurementId ? (
                  <>
                    {' • '}
                    <Link
                      href="#"
                      variant="secondary"
                      onFollow={(e) => {
                        e.preventDefault();
                        openCookieSettings();
                      }}
                    >
                      Cookie-Settings
                    </Link>
                  </>
                ) : null}
              </>
            }
          >
            <span className="app-header-title">
              <img
                className="app-header-title__logo"
                src="/favicon.svg"
                alt=""
              />
              <span className="app-header-title__text">Unicode Security Analyzer</span>
            </span>
          </Header>
        </div>
      }
    >
      <Tabs
        activeTabId={resolvedTabId}
        onChange={({ detail }) => {
          navigate(pathForTabId(detail.activeTabId));
        }}
        tabs={[
          {
            id: 'home',
            label: 'Home',
            content: <LandingPage onSelectTool={handleToolSelection} />,
          },
          {
            id: 'invisible-character-legend',
            label: 'Invisible Character Legend',
            content: <InvisibleCharacterLegendTool />,
          },
          {
            id: 'invisible-characters',
            label: 'Check text on invisible Characters',
            content: <InvisibleCharactersTool />,
          },
          {
            id: 'eml-msg-analyzer',
            label: 'Check EMail EML-File on invisible Characters',
            content: <MailEmlAnalyzerTool />,
          },
          {
            id: 'url-safety-checker',
            label: 'Check URL on invisible Characters',
            content: <UrlSafetyTool />,
          },
          {
            id: 'safe-links-decoder',
            label: 'M365 Safe Link Decoder',
            content: <SafeLinksTool />,
          },
          {
            id: 'mail-header-analyzer',
            label: 'Mail Header Analysis',
            content: <MailHeaderTool />,
          },
        ]}
      />
    </ContentLayout>
  );
};

export default HomePage;
